import { Badge, Button, Group, Highlight } from "@mantine/core";
import { DateTime } from "luxon";
import {
  MRT_ExpandButton,
  type MRT_Row,
  type MRT_TableInstance
} from "mantine-react-table-open";
import { generatePath, Link } from "react-router";
import { AssignmentType } from "../../../components/SiteTasks/OnboardingWizard/OnboardingWizard.constants";
import { OnboardingTodo } from "../../../components/Todos/Todos.constants";
import { getOnboardingTodoLabel } from "../../../components/Todos/utils/getOnboardingTodoLabel";
import { useSiteWizard } from "../../../hooks/useSiteWizard";
import { useSiteWizardMutations } from "../../../hooks/useSiteWizardMutations";
import { BadgePresets } from "../../../mantine/mantinePresets";
import { useToast } from "../../../mantine/notifications/useToast";
import { ROUTES } from "../../../routes";
import { getErrorText } from "../../../utils/get-error-text";
import type {
  ExpressOnboardingGenerator,
  ExpressOnboardingGeneratorsTableSiteTodoStatus
} from "../ExpressOnboarding.types";
import { getIsConfigurationTodoOpen } from "./utils/getIsConfigurationTodoOpen";
import { getSiteMissingData } from "./utils/getSiteMissingData";

interface ExpressOnboardingTableSiteCellProps {
  backendData?: Array<ExpressOnboardingGenerator>;
  siteTodosStatus?: ExpressOnboardingGeneratorsTableSiteTodoStatus;
  row: MRT_Row<ExpressOnboardingGenerator>;
  table: MRT_TableInstance<ExpressOnboardingGenerator>;
}

function ExpressOnboardingTableSiteCell({
  backendData,
  siteTodosStatus,
  row,
  table
}: ExpressOnboardingTableSiteCellProps) {
  const siteId = row.original.site_id;
  const backendSiteGenerators =
    backendData?.filter((generator) => generator.site_id === siteId) || [];

  const { isSiteMissingData } = getSiteMissingData(backendSiteGenerators);

  const siteUrl = generatePath(ROUTES.site.generators, {
    projectId: row.original.project_id,
    siteId: siteId.toString()
  });

  const isConfigurationTodoOpen = getIsConfigurationTodoOpen(siteTodosStatus);
  const isRegulatoryTodoOpen =
    !!siteTodosStatus?.regulatory_duty_todo_id &&
    !siteTodosStatus.regulatory_duty_todo_done;

  const isRegulatoryTodoClosable = isRegulatoryTodoOpen && !isSiteMissingData;
  const { data: siteWizard, isLoading: isSiteWizardLoading } = useSiteWizard(
    siteId,
    { enabled: isRegulatoryTodoClosable }
  );
  const isRegulatoryDataMissing =
    !!siteWizard?.phases.regulatory.missingData.length;

  const { updateOnboardingPhaseDoneMutation } = useSiteWizardMutations(siteId);
  const { showErrorToast } = useToast();

  function handleCloseRegulatoryTodo() {
    updateOnboardingPhaseDoneMutation.mutate(
      {
        onboardingPhase: getOnboardingTodoLabel(
          OnboardingTodo.REGULATORISCHE_DATEN_VERVOLLSTAENDIGEN,
          DateTime.now().year
        ),
        isDone: true
      },
      {
        onError: (error) => showErrorToast(getErrorText(error))
      }
    );
  }

  return (
    <Group gap="xs" w="100%">
      <MRT_ExpandButton row={row} table={table} />
      <Link target="_blank" to={siteUrl} onClick={(e) => e.stopPropagation()}>
        <Highlight
          component="span"
          highlight={table.getState().globalFilter ?? ""}
          inherit
        >
          {`${row.original.site_name} (${siteId})`}
        </Highlight>
      </Link>
      {isConfigurationTodoOpen ? (
        <>
          <Badge {...BadgePresets.error}>
            Erst {AssignmentType.Configuration} bestätigen
          </Badge>

          <Button
            component={Link}
            ms="auto"
            size="xs"
            target="_blank"
            to={siteUrl}
            variant="subtle"
            onClick={(e) => e.stopPropagation()}
          >
            {AssignmentType.Configuration} prüfen
          </Button>
        </>
      ) : (
        <>
          {isSiteMissingData ? (
            <Badge {...BadgePresets.warning}>Daten fehlen</Badge>
          ) : (
            <Badge {...BadgePresets.success}>Daten vollständig</Badge>
          )}

          {isRegulatoryTodoClosable &&
            (isRegulatoryDataMissing ? (
              <Button
                component={Link}
                ms="auto"
                size="xs"
                target="_blank"
                to={siteUrl}
                variant="subtle"
                onClick={(e) => e.stopPropagation()}
              >
                {AssignmentType.Regulatory} prüfen
              </Button>
            ) : (
              <Button
                loading={
                  isSiteWizardLoading ||
                  updateOnboardingPhaseDoneMutation.isPending
                }
                ms="auto"
                size="xs"
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseRegulatoryTodo();
                }}
              >
                {AssignmentType.Regulatory} Aufgabe als erledigt markieren
              </Button>
            ))}
        </>
      )}
    </Group>
  );
}

export { ExpressOnboardingTableSiteCell };
